import { Package2, X, TrendingUp, TrendingDown } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { ICompanyItemInterface } from '../../api/CompanyItems.interface';

type Props = {
  minQty: number | '';
  maxQty: number | '';
  setMinQty: (value: number | '') => void;
  setMaxQty: (value: number | '') => void;
  filterData: ICompanyItemInterface[] | null;
};

const QuantityFilter = ({
  minQty,
  maxQty,
  setMinQty,
  setMaxQty,
  filterData,
}: Props) => {
  const minValue =
    filterData && filterData.length > 0
      ? Math.min(...filterData.map((item) => item.Qty))
      : undefined;

  const maxValue =
    filterData && filterData.length > 0
      ? Math.max(...filterData.map((item) => item.Qty))
      : undefined;

  const avgValue =
    filterData && filterData.length > 0
      ? Math.round(filterData.reduce((sum, item) => sum + item.Qty, 0) / filterData.length)
      : undefined;

  const handleMinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setMinQty(value ? parseInt(value) : '');
  };

  const handleMaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setMaxQty(value ? parseInt(value) : '');
  };

  const clearFilters = () => {
    setMinQty('');
    setMaxQty('');
  };

  const setPreset = (min: number | '', max: number | '') => {
    setMinQty(min);
    setMaxQty(max);
  };

  const hasFilters = minQty !== '' || maxQty !== '';

  return (
    <Card className="w-full border-0 bg-gradient-to-br from-muted/30 to-muted/10 p-3">
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="rounded-lg bg-primary/10 p-1.5">
              <Package2 className="h-4 w-4 text-primary" />
            </div>
            <div>
              <h3 className="text-sm font-semibold">تصفية الكمية</h3>
              <p className="text-xs text-muted-foreground">
                حدد نطاق الكميات المطلوب
              </p>
            </div>
          </div>
          
          {hasFilters && (
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 hover:bg-destructive/10 hover:text-destructive"
              onClick={clearFilters}
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>

        {minValue !== undefined && maxValue !== undefined && (
          <div className="grid grid-cols-3 gap-2 p-2 bg-muted/20 rounded-lg">
            <div className="text-center">
              <p className="text-[10px] text-muted-foreground mb-0.5">الحد الأدنى</p>
              <Badge variant="outline" className="bg-background/50 text-xs">
                <TrendingDown className="h-3 w-3 ml-1" />
                {minValue}
              </Badge>
            </div>
            <div className="text-center">
              <p className="text-[10px] text-muted-foreground mb-0.5">المتوسط</p>
              <Badge variant="outline" className="bg-background/50 text-xs">
                {avgValue}
              </Badge>
            </div>
            <div className="text-center">
              <p className="text-[10px] text-muted-foreground mb-0.5">الحد الأقصى</p>
              <Badge variant="outline" className="bg-background/50 text-xs">
                <TrendingUp className="h-3 w-3 ml-1" />
                {maxValue}
              </Badge>
            </div>
          </div>
        )}

        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">من</label>
              <div className="relative">
                <Input
                  type="number"
                  placeholder={minValue !== undefined ? `${minValue}` : 'الحد الأدنى'}
                  value={minQty}
                  onChange={handleMinChange}
                  className={cn(
                    "h-9 text-sm pr-3 pl-8 bg-background/50 border-border/50 focus:bg-background transition-colors",
                    minQty !== '' && "border-primary/30 bg-primary/5"
                  )}
                />
                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                  قطعة
                </span>
              </div>
            </div>
            
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">إلى</label>
              <div className="relative">
                <Input
                  type="number"
                  placeholder={maxValue !== undefined ? `${maxValue}` : 'الحد الأقصى'}
                  value={maxQty}
                  onChange={handleMaxChange}
                  className={cn(
                    "h-9 text-sm pr-3 pl-8 bg-background/50 border-border/50 focus:bg-background transition-colors",
                    maxQty !== '' && "border-primary/30 bg-primary/5"
                  )}
                />
                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                  قطعة
                </span>
              </div>
            </div>
          </div>

          {minValue !== undefined && maxValue !== undefined && (
            <div className="grid grid-cols-2 gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPreset('', 10)}
                className="h-7 text-xs hover:bg-primary/10 hover:text-primary hover:border-primary/30"
              >
                أقل من 10
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPreset(10, 50)}
                className="h-7 text-xs hover:bg-primary/10 hover:text-primary hover:border-primary/30"
              >
                10-50
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPreset(50, 100)}
                className="h-7 text-xs hover:bg-primary/10 hover:text-primary hover:border-primary/30"
              >
                50-100
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPreset(100, '')}
                className="h-7 text-xs hover:bg-primary/10 hover:text-primary hover:border-primary/30"
              >
                أكثر من 100
              </Button>
            </div>
          )}
        </div>

        {hasFilters && (
          <div className="pt-2 border-t border-border/30">
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">النطاق المحدد:</span>
              <div className="flex items-center gap-2">
                {minQty !== '' && (
                  <Badge variant="outline" className="bg-primary/5 border-primary/20 text-primary text-xs">
                    من {minQty}
                  </Badge>
                )}
                {maxQty !== '' && (
                  <Badge variant="outline" className="bg-primary/5 border-primary/20 text-primary text-xs">
                    إلى {maxQty}
                  </Badge>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};

export default QuantityFilter;